import * as React from 'react';
import { Enter, Space } from '@fluentui/keyboard-keys';
import {
  Button,
  mergeCallbacks,
  Persona,
  slot,
  Tooltip,
  useFluent,
  useId,
  useMergedRefs,
} from '@fluentui/react-components';
import type { GroundingMenuItemProps, GroundingMenuItemState } from './GroundingMenuItem.types';
import type { SelectionEvents, OptionValue } from '@fluentui-copilot/react-input-listbox';
import { useInputListboxContext } from '@fluentui-copilot/react-input-listbox';
import { useDesignVersion } from '@fluentui-copilot/react-provider';
import {
  bundleIcon,
  ChevronLeftFilled,
  ChevronLeftRegular,
  ChevronRightFilled,
  ChevronRightRegular,
} from '@fluentui/react-icons';

const ChevronRightIcon = bundleIcon(ChevronRightFilled, ChevronRightRegular);
const ChevronLeftIcon = bundleIcon(ChevronLeftFilled, ChevronLeftRegular);

/**
 * Create the state required to render GroundingMenuItem.
 *
 * The returned state can be modified with hooks such as useGroundingMenuItemStyles_unstable,
 * before being passed to renderGroundingMenuItem_unstable.
 *
 * @param props - props from this instance of GroundingMenuItem
 * @param ref - reference to root HTMLElement of GroundingMenuItem
 */
export const useGroundingMenuItem_unstable = (
  props: GroundingMenuItemProps,
  ref: React.Ref<HTMLSpanElement>,
): GroundingMenuItemState => {
  const { className, style, ...primarySlotProps } = props;
  const { disabled, value, onOptionSelect } = primarySlotProps;
  const designVersion = useDesignVersion(props.designVersion);
  const { dir } = useFluent();

  const optionRef = React.useRef<HTMLDivElement>(null);
  const rootRef = useMergedRefs(optionRef, ref);

  // use the id if provided, otherwise use a generated id
  const id = useId('grounding-menu-item', props.id);

  const optionData = React.useRef<OptionValue | undefined>(undefined);

  const selectOption = useInputListboxContext(ctx => ctx.selectOption);
  const selectItem = React.useCallback(
    (event: SelectionEvents) => {
      if (disabled) {
        event.preventDefault();
        return;
      }

      if (optionData.current) {
        selectOption(event, optionData.current);
      }
    },
    [disabled, selectOption],
  );

  const onKeyDown: React.KeyboardEventHandler<HTMLDivElement> = React.useCallback(
    event => {
      if (event.key === Enter || event.key === Space) {
        selectItem(event);
        event.preventDefault();
      }
    },
    [selectItem],
  );

  // Resolve the slot early to ensure we don't overwrite a user provided ref
  const primaryText = slot.optional(props.primaryText, { elementType: 'span' });
  const textRef = React.useRef<HTMLSpanElement>(undefined);
  const primaryTextRef = useMergedRefs(primaryText?.ref, textRef);
  if (primaryText) {
    primaryText.ref = primaryTextRef;
  }

  // register option data with context
  const registerOption = useInputListboxContext(ctx => ctx.registerOption);
  React.useEffect(() => {
    if (id && optionRef.current && textRef.current) {
      const optionText = textRef.current.textContent ?? '';
      const optionValue = value ?? optionText;

      // Save a reference to the option data so other callbacks can access it
      optionData.current = { id, disabled, text: optionText, value: optionValue, onOptionSelect };
      const unregisterOption = registerOption(optionData.current, optionRef.current);
      return () => {
        unregisterOption();
        optionData.current = undefined;
      };
    }
  }, [disabled, id, onOptionSelect, registerOption, value]);

  const selected = useInputListboxContext(ctx => {
    const selectedOptions = ctx.selectedOptions;

    return (
      optionData.current?.value !== undefined &&
      selectedOptions.find(o => o === optionData.current?.value) !== undefined
    );
  });

  const state: GroundingMenuItemState = {
    designVersion,
    disabled,
    selected,
    components: { root: 'div', persona: Persona, submenuButton: Button, submenuButtonTooltip: Tooltip },
    root: slot.always(props.root, {
      defaultProps: { className, style },
      elementType: 'div',
    }),
    persona: slot.always(props.persona, {
      defaultProps: {
        'aria-disabled': disabled ? true : undefined,
        role: 'menuitem',
        id,
        tabIndex: 0,
        ...primarySlotProps,
        primaryText,
        onKeyDown: mergeCallbacks(onKeyDown, props.onKeyDown),
        onClick: mergeCallbacks(selectItem, props.onClick),
        ref: rootRef,
      },
      elementType: Persona,
    }),
    submenuButton: slot.optional(props.submenuButton, {
      defaultProps: {
        appearance: 'transparent',
        'aria-haspopup': 'menu',
        role: 'menuitem',
        icon: dir === 'ltr' ? <ChevronRightIcon /> : <ChevronLeftIcon />,
      },
      elementType: Button,
    }),
    submenuButtonTooltip: slot.optional(props.submenuButtonTooltip, {
      defaultProps: { relationship: 'label' },
      elementType: Tooltip,
    }),
  };

  if (state.submenuButton) {
    state.root.role = state.root.role ?? 'group';
    state.submenuButton.onClick = mergeCallbacks(
      state.submenuButton.onClick as React.MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>,
      ev => {
        ev.stopPropagation();
      },
    );
    state.submenuButton.onKeyDown = mergeCallbacks(
      state.submenuButton.onKeyDown as React.KeyboardEventHandler<HTMLAnchorElement | HTMLButtonElement>,
      ev => {
        if (ev.key === Enter) {
          ev.stopPropagation();
        }
      },
    );
  }

  return state;
};
